import { useCallback, useEffect, useState } from 'react';
import { StringParam, useQueryParams } from 'use-query-params';
import { extractBasePanelKey, extractSplitVariableInfo } from '@/dashboards/utils';
import { useExpandPanelContext } from '@/plugin-system';
import { usePanelActions, usePanelEditor } from './dashboard-provider-api';
import { useFindPanelGroupItemId } from './find-panel';

/**
 * Updates viewPanel and editPanel query params, controls expanded panel state.
 */
export function useUpdateExpandPanelParams() {
  const [, setExpandPanelParams] = useQueryParams({
    editPanel: StringParam,
    viewPanel: StringParam,
  });

  return useCallback(
    ({ viewPanel, editPanel }: { viewPanel?: string; editPanel?: string }) => {
      setExpandPanelParams({ viewPanel, editPanel });
    },
    [setExpandPanelParams]
  );
}

export enum ExpandedPanelTabs {
  View = 0,
  Edit = 1,
}

/**
 * Controls query params for viewPanel / editPanel, and selected tab state.
 * editPanel can be set from direct link or edit icon in panel header.
 * viewPanel can be set from direct link, drilldown from pinned tooltip in TimeSeriesChart,
 * or clicking expand icon in the panel header.
 */
export function useSyncExpandPanelState() {
  const [expandPanelParams] = useQueryParams({
    editPanel: StringParam,
    viewPanel: StringParam,
  });
  const updateExpandPanelParams = useUpdateExpandPanelParams();

  const [selectedTab, setSelectedTab] = useState<ExpandedPanelTabs>();

  const panelEditor = usePanelEditor();
  const { expandedPanelKey, setExpandedPanelKey } = useExpandPanelContext();

  const panelKey = expandPanelParams.editPanel ?? expandPanelParams.viewPanel ?? expandedPanelKey;
  const basePanelKey = extractBasePanelKey(panelKey);
  const { splitVariableName, splitVariableValue } = extractSplitVariableInfo(panelKey);

  // Lookup panel group item id when expand dialog accessed from direct link or drilldown.
  // Use base panelKey without split variable info since that is what is stored in dashboard spec.
  const expandedPanelGroupItemId = useFindPanelGroupItemId(basePanelKey);

  // Fallback unlikely to be used, extra canOpenEditPanel check ensures we do not use invalid id
  const { openEditPanel } = usePanelActions(
    expandedPanelGroupItemId ?? { panelGroupId: 0, panelGroupItemLayoutId: '1' }
  );

  // Sync selected tab state with expanded panel params
  useEffect(() => {
    // Ensure we have a valid panel group item id
    const canOpenEditPanel = expandedPanelGroupItemId !== undefined;

    if (expandPanelParams.viewPanel && expandPanelParams.editPanel) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedTab(ExpandedPanelTabs.Edit);
      if (panelEditor === undefined && canOpenEditPanel) {
        openEditPanel();
      }
    } else if (panelEditor?.mode === 'Add') {
      // Add new panel shows expanded dialog with edit tab selected
      setSelectedTab(ExpandedPanelTabs.Edit);
    } else if (expandPanelParams.editPanel && selectedTab !== ExpandedPanelTabs.Edit) {
      setSelectedTab(ExpandedPanelTabs.Edit);
      if (panelEditor === undefined && canOpenEditPanel) {
        openEditPanel();
      }
    } else if (expandPanelParams.viewPanel && selectedTab !== ExpandedPanelTabs.View) {
      setSelectedTab(ExpandedPanelTabs.View);
      if (expandedPanelKey === undefined) {
        setExpandedPanelKey(expandPanelParams.viewPanel);
      }
    } else if (selectedTab === undefined && expandedPanelKey !== undefined) {
      // Sync viewPanel query param when expandedPanelKey changes, important so expand from pinned tooltip works
      updateExpandPanelParams({ viewPanel: expandedPanelKey });
      setSelectedTab(ExpandedPanelTabs.View);
    } else if (!expandPanelParams.editPanel && !expandPanelParams.viewPanel) {
      if (panelEditor?.mode === 'Edit') {
        // Close edit panel dialog when editPanel query param is removed
        panelEditor.close();
        setSelectedTab(undefined);
      } else if (expandedPanelKey !== undefined && selectedTab !== undefined) {
        // Close view panel with back button only if it was explicitly opened
        setExpandedPanelKey(undefined);
        setSelectedTab(undefined);
      }
    }
  }, [
    expandPanelParams.viewPanel,
    expandPanelParams.editPanel,
    selectedTab,
    panelEditor,
    expandedPanelKey,
    setExpandedPanelKey,
    expandedPanelGroupItemId,
    openEditPanel,
    updateExpandPanelParams,
  ]);

  return {
    panelKey,
    basePanelKey,
    splitVariableName,
    splitVariableValue,
    expandedPanelGroupItemId,
    selectedTab,
    setSelectedTab,
  };
}
